import { Fragment, useEffect, useState } from "react";
import { ApiError, api, type CasoDetalle as CasoT, type ImeiOut } from "@/lib/api";
import { canEdit, useAuth } from "@/lib/auth";
import { ESTADO_IMEI_LABEL, ESTADO_IMEI_PILL, type EstadoImeiUI } from "@/lib/imei";
import ConfirmDialog from "@/ui/ConfirmDialog";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";
import ConfiguracionEquipoPanel from "@/components/ConfiguracionEquipoPanel";
import { DetalleVenta, MarcaVenta } from "@/components/AvisoVentasImeis";
import { ayudaImeis } from "@/components/ayuda/vistas/imeis";
import AyudaVista from "@/ui/AyudaVista";
import { verificarVentas, type VerificacionVenta } from "@/lib/ventas";

/**
 * Tab de IMEIs (equipos) de un caso: lista + estado por IMEI. En casos
 * ABIERTOS permite agregar (modal), editar, quitar y cambiar el estado de los
 * IMEIs; los cerrados son de solo lectura, botones de estado incluidos (el
 * servidor también lo exige desde 2026-10-13). Para un equipo nuevo se usa
 * "+ Caso relacionado". El modelo NO
 * se pide por IMEI: se hereda del modelo del caso.
 */
// Orden y etiqueta corta de los estados manuales (PROVEEDOR -> PROV por espacio).
const ESTADOS_ORDEN: EstadoImeiUI[] = ["OK", "REV", "CLI", "PROVEEDOR", "CRIT"];
const ESTADO_CORTO: Record<EstadoImeiUI, string> = {
  OK: "OK",
  REV: "REV",
  CLI: "CLI",
  PROVEEDOR: "PROV",
  CRIT: "CRIT",
};

// Segmented control de estado del IMEI: botones de color (el activo resaltado
// según estado_manual) + "Auto" para limpiar el manual y volver al semáforo
// por antigüedad. Misma fuente de verdad de colores que el resto (lib/imei).
// Qué significa cada uno lo dice la Ayuda de la pestaña (cero tooltips).
function BotonesEstado({
  imei,
  onSet,
}: {
  imei: ImeiOut;
  onSet: (e: EstadoImeiUI | "") => void;
}) {
  const activo = (imei.estado_manual ?? null) as EstadoImeiUI | null;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {ESTADOS_ORDEN.map((e) => {
        const on = activo === e;
        return (
          <button
            key={e}
            type="button"
            onClick={() => onSet(e)}
            className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border transition ${
              on ? ESTADO_IMEI_PILL[e] : "border-ink-600 text-zinc-400 hover:text-white"
            }`}
          >
            {ESTADO_CORTO[e]}
          </button>
        );
      })}
      <button
        type="button"
        onClick={() => onSet("")}
        className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border transition ${
          activo == null
            ? "bg-zinc-500/15 text-zinc-200 border-zinc-500/40"
            : "border-ink-600 text-zinc-400 hover:text-white"
        }`}
      >
        Auto
      </button>
    </div>
  );
}

// Ficha de "Datos de referencia" por IMEI (Req 2): texto editable que se
// mantiene al día (tel. del cliente, contacto en sitio…). NO es bitácora ni
// evidencia. Una sola ficha por equipo (PATCH /imeis/{id} con ficha_referencia).
function FichaReferencia({
  imei,
  casoId,
  editable,
  onSaved,
}: {
  imei: ImeiOut;
  casoId: number;
  editable: boolean;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [editando, setEditando] = useState(false);
  const [val, setVal] = useState(imei.ficha_referencia ?? "");
  const [saving, setSaving] = useState(false);
  const ficha = imei.ficha_referencia;

  async function guardar() {
    setSaving(true);
    try {
      await api.patch(`/api/casos/${casoId}/imeis/${imei.id}`, {
        ficha_referencia: val.trim() || null,
      });
      toast.success("Datos de referencia guardados");
      setEditando(false);
      onSaved();
    } catch (err) {
      toast.error("No se pudo guardar", err instanceof ApiError ? err.detail : "Error");
    } finally {
      setSaving(false);
    }
  }

  if (editando) {
    return (
      <div className="space-y-1">
        <textarea
          className="input text-xs min-h-[60px] w-full"
          placeholder="Datos de referencia SOLO de este equipo (tel. del cliente, contacto en sitio…). No es evidencia ni bitácora."
          value={val}
          onChange={(e) => setVal(e.target.value)}
          autoFocus
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void guardar()}
            disabled={saving}
            className="btn-primary text-xs disabled:opacity-50"
          >
            {saving ? "Guardando…" : "Guardar"}
          </button>
          <button type="button" onClick={() => setEditando(false)} className="btn-ghost text-xs">
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  if (!ficha && !editable) return null; // cerrado y sin datos: no ocupa espacio

  return (
    <div className="text-xs">
      <span className="text-[10px] uppercase tracking-wide text-zinc-500">📌 Datos de referencia</span>{" "}
      {ficha ? (
        <span className="text-zinc-300 whitespace-pre-wrap break-words">{ficha}</span>
      ) : (
        <span className="text-zinc-600 italic">solo de este equipo (ej. tel. del cliente)</span>
      )}
      {editable && (
        <button
          type="button"
          onClick={() => {
            setVal(ficha ?? "");
            setEditando(true);
          }}
          className="ml-2 text-accent-300 hover:text-accent-200"
        >
          {ficha ? "Editar" : "Agregar"}
        </button>
      )}
    </div>
  );
}

export default function TabImeis({
  caso,
  onChanged,
  onAgregar,
}: {
  caso: CasoT;
  onChanged: () => void;
  onAgregar: () => void;
}) {
  const me = useAuth((s) => s.user);
  const toast = useToast();
  const puedeEditar = canEdit(me) && caso.estado !== "Cerrado";

  const [editando, setEditando] = useState<ImeiOut | null>(null);
  const [editImei, setEditImei] = useState("");
  const [guardandoEdit, setGuardandoEdit] = useState(false);

  const [borrar, setBorrar] = useState<ImeiOut | null>(null);

  function abrirEdicion(i: ImeiOut) {
    setEditando(i);
    setEditImei(i.imei);
  }

  async function guardarEdicion() {
    if (!editando) return;
    if (editImei.trim().length < 5) {
      toast.error("El IMEI debe tener al menos 5 caracteres");
      return;
    }
    setGuardandoEdit(true);
    try {
      await api.patch(`/api/casos/${caso.id}/imeis/${editando.id}`, {
        imei: editImei.trim(),
      });
      toast.success("IMEI actualizado");
      setEditando(null);
      onChanged();
    } catch (err) {
      toast.error("No se pudo editar", err instanceof ApiError ? err.detail : "Error");
    } finally {
      setGuardandoEdit(false);
    }
  }

  async function cambiarEstado(imeiId: number, nuevo: EstadoImeiUI | "") {
    try {
      await api.patch(`/api/casos/${caso.id}/imeis/${imeiId}/estado`, {
        estado: nuevo === "" ? null : nuevo,
      });
      toast.success("Estado actualizado");
      onChanged();
    } catch (err) {
      toast.error("No se pudo actualizar", err instanceof ApiError ? err.detail : "Error");
    }
  }

  async function confirmarBorrado() {
    if (!borrar) return;
    try {
      await api.del(`/api/casos/${caso.id}/imeis/${borrar.id}`);
      toast.success("IMEI quitado");
      onChanged();
    } catch (err) {
      toast.error("No se pudo quitar", err instanceof ApiError ? err.detail : "Error");
    }
  }

  // ¿Los vendimos? Marca por IMEI contra el catálogo de Ventas
  // (informativa; si el fetch falla, simplemente no se pinta).
  const [ventasCheck, setVentasCheck] = useState<Record<string, VerificacionVenta>>({});
  const imeisKey = caso.imeis.map((i) => i.imei).join(",");
  useEffect(() => {
    const imeis = imeisKey ? imeisKey.split(",") : [];
    if (imeis.length === 0) {
      setVentasCheck({});
      return;
    }
    verificarVentas(imeis)
      .then((rs) => setVentasCheck(Object.fromEntries(rs.map((r) => [r.valor, r]))))
      .catch(() => setVentasCheck({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imeisKey]);

  // El modelo del IMEI se hereda del caso (no se captura por equipo).
  const modeloDe = (i: ImeiOut) => i.modelo_real || caso.modelo;
  // Los IMEIs en CRÍTICO flotan al tope (orden estable conserva el resto).
  const imeisOrdenados = [...caso.imeis].sort((a, b) => {
    const ra = (a.estado_efectivo ?? a.estado_manual ?? "REV") === "CRIT" ? 0 : 1;
    const rb = (b.estado_efectivo ?? b.estado_manual ?? "REV") === "CRIT" ? 0 : 1;
    return ra - rb;
  });



  const accionesFila = (i: ImeiOut) =>
    puedeEditar && (
      <>
        <button onClick={() => abrirEdicion(i)} className="btn-ghost text-xs">
          Editar
        </button>
        <button
          onClick={() => setBorrar(i)}
          className="btn-ghost text-xs text-red-300 hover:text-red-200"
        >
          Quitar
        </button>
      </>
    );

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <AyudaVista
          titulo="IMEIs"
          {...ayudaImeis({ puedeEditar, cerrado: caso.estado === "Cerrado" })}
        />
      </div>
      {caso.tipo_categoria === "configuracion-dispositivos" && (
        <ConfiguracionEquipoPanel caso={caso} editable={puedeEditar} />
      )}
      <div className="card space-y-4">
      {caso.imeis.length === 0 ? (
        <div className="space-y-3">
          <p className="text-sm text-zinc-500">
            {puedeEditar
              ? "Este caso no tiene IMEIs."
              : "Este caso no tiene IMEIs registrados."}
          </p>
          {puedeEditar && (
            <button onClick={onAgregar} className="btn-primary text-sm w-full sm:w-auto">
              + Agregar IMEI
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Mobile: cards */}
          <ul className="lg:hidden divide-y divide-ink-700">
            {imeisOrdenados.map((i) => {
              const estado = (i.estado_efectivo ?? i.estado_manual ?? "REV") as EstadoImeiUI;
              return (
                <li key={i.id} className="py-2.5 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-sm text-zinc-200 break-all">{i.imei}</span>
                    <MarcaVenta check={ventasCheck[i.imei]} />
                    <span className={`shrink-0 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border ${ESTADO_IMEI_PILL[estado]}`}>
                      {estado}
                    </span>
                  </div>
                  <div className="text-xs text-zinc-400">
                    {modeloDe(i) || "Sin modelo"} · {ESTADO_IMEI_LABEL[estado]}
                  </div>
                  <DetalleVenta check={ventasCheck[i.imei]} />
                  <div className="text-xs text-zinc-500">
                    Última revisión: {i.ultima_revision ? new Date(i.ultima_revision).toLocaleString() : "—"}
                    {i.dias_sin_movimiento != null && i.dias_sin_movimiento > 0 && (
                      <> · {i.dias_sin_movimiento} día{i.dias_sin_movimiento === 1 ? "" : "s"} sin movimiento</>
                    )}
                  </div>
                  {puedeEditar && (
                    <div className="flex flex-wrap items-center gap-1.5 pt-1">
                      <BotonesEstado imei={i} onSet={(e) => void cambiarEstado(i.id, e)} />
                      {accionesFila(i)}
                    </div>
                  )}
                  <FichaReferencia
                    imei={i}
                    casoId={caso.id}
                    editable={puedeEditar}
                    onSaved={onChanged}
                  />
                </li>
              );
            })}
          </ul>

          {/* Desktop: tabla */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-zinc-500 text-left">
                <tr>
                  <th className="py-2 pr-4">IMEI</th>
                  <th className="py-2 pr-4">Modelo</th>
                  <th className="py-2 pr-4">Estado</th>
                  <th className="py-2 pr-4">Última revisión</th>
                  {puedeEditar && <th className="py-2 pr-4">Acciones</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-700">
                {imeisOrdenados.map((i) => {
                  const estado = (i.estado_efectivo ?? i.estado_manual ?? "REV") as EstadoImeiUI;
                  return (
                    <Fragment key={i.id}>
                    <tr>
                      <td className="py-2 pr-4 font-mono text-zinc-200">
                        <span className="inline-flex items-center gap-2">
                          {i.imei}
                          <MarcaVenta check={ventasCheck[i.imei]} />
                        </span>
                        <DetalleVenta check={ventasCheck[i.imei]} />
                      </td>
                      <td className="py-2 pr-4 text-zinc-300">{modeloDe(i) || "-"}</td>
                      <td className="py-2 pr-4">
                        <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border ${ESTADO_IMEI_PILL[estado]}`}>
                          {estado} · {ESTADO_IMEI_LABEL[estado]}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-xs text-zinc-500">
                        {i.ultima_revision ? new Date(i.ultima_revision).toLocaleString() : "—"}
                      </td>
                      {puedeEditar && (
                        <td className="py-2 pr-4">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <BotonesEstado imei={i} onSet={(e) => void cambiarEstado(i.id, e)} />
                            {accionesFila(i)}
                          </div>
                        </td>
                      )}
                    </tr>
                    {(i.ficha_referencia || puedeEditar) && (
                      <tr>
                        <td colSpan={puedeEditar ? 5 : 4} className="pb-2 pr-4">
                          <FichaReferencia
                            imei={i}
                            casoId={caso.id}
                            editable={puedeEditar}
                            onSaved={onChanged}
                          />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {puedeEditar && (
            <div className="border-t border-ink-700 pt-3">
              <button onClick={onAgregar} className="btn-primary text-sm w-full sm:w-auto">
                + Agregar IMEI
              </button>
            </div>
          )}
        </>
      )}

      <Modal
        open={editando !== null}
        onOpenChange={(v) => !v && setEditando(null)}
        title="Editar IMEI"
        description="El modelo se hereda del caso; aquí solo se corrige el número."
        size="sm"
        footer={
          <>
            <button onClick={() => setEditando(null)} className="btn-ghost text-sm">
              Cancelar
            </button>
            <button
              onClick={() => void guardarEdicion()}
              disabled={guardandoEdit}
              className="btn-primary text-sm disabled:opacity-50"
            >
              {guardandoEdit ? "Guardando…" : "Guardar"}
            </button>
          </>
        }
      >
        <label className="block space-y-1">
          <span className="text-xs text-zinc-400">IMEI</span>
          <input
            value={editImei}
            onChange={(e) => setEditImei(e.target.value)}
            autoFocus
            className="input font-mono"
          />
        </label>
      </Modal>

      <ConfirmDialog
        open={borrar !== null}
        onOpenChange={(v) => !v && setBorrar(null)}
        title="Quitar IMEI"
        description={
          borrar
            ? `¿Quitar el IMEI ${borrar.imei} de este caso? Su historial de estado se elimina; queda una nota en la bitácora.`
            : ""
        }
        confirmLabel="Quitar"
        variant="danger"
        onConfirm={confirmarBorrado}
      />
      </div>
    </div>
  );
}
